import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Button,
  Divider,
  CircularProgress,
  Alert,
  Snackbar,
  Chip,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import IntegrationInstructionsIcon from "@mui/icons-material/IntegrationInstructions";
import API from "../../utils/Axios.utils";


const PORTAL_DEFINITIONS = [
  {
    slug: "acx",
    label: "ACX",
    color: "#1976d2",
    fields: [],
    note: "Webhook-based — no credentials needed",
  },
  {
    slug: "naukri",
    label: "Naukri",
    color: "#2e7d32",
    fields: [
      { key: "api_key", label: "API Key" },
      { key: "client_id", label: "Client ID" },
      { key: "secret", label: "Secret", type: "password" },
    ],
  },
  {
    slug: "indeed",
    label: "Indeed",
    color: "#6a1b9a",
    fields: [
      { key: "publisher_id", label: "Publisher ID" },
      { key: "api_token", label: "API Token", type: "password" },
    ],
  },
  {
    slug: "linkedin",
    label: "LinkedIn",
    color: "#0077b5",
    fields: [
      { key: "client_id", label: "Client ID" },
      { key: "client_secret", label: "Client Secret", type: "password" },
    ],
  },
];

const PIPELINE_DEFAULTS = {
  pipeline_type: "",
  l1_required: false,
  l2_required: false,
  inbound_enabled: false,
  resume_score_threshold: 70,
  resume_unavailable_action: "",
  voice_callback_url: "",
  ts_send_credentials_via: "acx_email",
  te_api_url: "",
  te_api_token: "",
  nylas_api_key: "",
  nylas_grant_id: "",
  credential_email_subject: "",
  credential_email_body: "",
};

// Tokens the client can drop into the subject/body — substituted by Sahil's
// team's service (not this app) when the credentials email actually goes out.
const CREDENTIAL_EMAIL_PLACEHOLDERS = [
  { token: "{{username}}", hint: "Candidate's username" },
  { token: "{{email}}", hint: "Candidate's login email" },
  { token: "{{password}}", hint: "Candidate's generated password" },
  { token: "{{ts_link}}", hint: "Candidate portal login URL" },
];

// The credentials service reads `credential_email_body` straight from the DB and
// hands it to Nylas as the email's HTML body, so the stored value must be HTML.
// Users author the body as plain text in the UI, though: `plainTextToHtml`
// converts it on save (escaping markup, blank lines -> <p>, newlines -> <br/>)
// and `htmlToPlainText` converts an existing value back when it loads into the
// editor. The {{...}} tokens contain no markup, so they survive both directions.
const CREDENTIAL_EMAIL_BODY_PLACEHOLDER = `Hi {{username}},

Your Training Simulation portal account is ready. Here are your login details:

Email: {{email}}
Password: {{password}}
Login here: {{ts_link}}

Regards,
Training Simulation Team`;

// Escape the three characters a mail client would read as markup, so the user's
// literal text survives being dropped into an HTML body.
const escapeHtml = (s) =>
  String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// Plain text (as typed in the UI) -> HTML (as stored and emailed). A run of one
// or more blank lines separates <p> paragraphs; single newlines become <br/>.
const plainTextToHtml = (text) => {
  const trimmed = String(text || "").replace(/\r\n/g, "\n").trim();
  if (!trimmed) return "";
  const body = trimmed
    .split(/\n{2,}/)
    .map((block) => `  <p>${escapeHtml(block).replace(/\n/g, "<br/>")}</p>`)
    .join("\n");
  return `<div style="font-family:'Noto Sans',Arial,sans-serif;color:#333;max-width:480px;margin:0 auto;">\n${body}\n</div>`;
};

// HTML (as stored) -> plain text (for the editor). Best-effort inverse of
// plainTextToHtml; also flattens any hand-written HTML saved before this field
// became plain-text. A value with no tags is already plain text and passes
// through unchanged.
const htmlToPlainText = (html) => {
  const raw = String(html || "");
  if (!/<[a-z][\s\S]*>/i.test(raw)) return raw;
  return raw
    .replace(/<\s*br\s*\/?\s*>/gi, "\n")
    .replace(/<\s*\/\s*(p|div|tr|h[1-6]|li)\s*>/gi, "\n\n")
    .replace(/<\s*\/\s*td\s*>/gi, " ")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&amp;/gi, "&")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

const PORTALS_DEFAULT = PORTAL_DEFINITIONS.reduce((acc, p) => {
  acc[p.slug] = { enabled: false };
  return acc;
}, {});

const SectionHeader = ({ icon, title, subtitle }) => (
  <Box mb={3}>
    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
      {icon}
      <Typography variant="h6" fontWeight={700}>
        {title}
      </Typography>
    </Box>
    {subtitle && (
      <Typography variant="body2" color="text.secondary" ml={4}>
        {subtitle}
      </Typography>
    )}
    <Divider sx={{ mt: 2 }} />
  </Box>
);

const FieldLabel = ({ children, required }) => (
  <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
    {children}
    {required && <span style={{ color: "red", marginLeft: 2 }}>*</span>}
  </Typography>
);

const PipelineConfig = () => {
  const [pipelineForm, setPipelineForm] = useState(PIPELINE_DEFAULTS);
  const [portals, setPortals] = useState(PORTALS_DEFAULT);
  const [loading, setLoading] = useState(true);
  const [savingPipeline, setSavingPipeline] = useState(false);
  const [savingPortals, setSavingPortals] = useState(false);
  const [generatingToken, setGeneratingToken] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const showSnackbar = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  useEffect(() => {
    const loadAll = async () => {
      try {
        const [pipelineRes, portalsRes] = await Promise.all([
          API.get("/api/config/pipeline"),
          API.get("/api/config/portal-integrations"),
        ]);

        if (pipelineRes.data) {
          const d = pipelineRes.data;
          setPipelineForm({
            pipeline_type: d.pipeline_type || "",
            l1_required: d.l1_required ?? false,
            l2_required: d.l2_required ?? false,
            inbound_enabled: d.inbound_enabled ?? false,
            resume_score_threshold: d.resume_score_threshold ?? 70,
            resume_unavailable_action: d.resume_unavailable_action || "",
            voice_callback_url: d.voice_callback_url || "",
            ts_send_credentials_via: d.ts_send_credentials_via || "acx_email",
            te_api_url: d.te_api_url || "",
            te_api_token: d.te_api_token || "",
            nylas_api_key: d.nylas_api_key || "",
            nylas_grant_id: d.nylas_grant_id || "",
            credential_email_subject: d.credential_email_subject || "",
            credential_email_body: htmlToPlainText(d.credential_email_body || ""),
          });
        }

        if (portalsRes.data?.portal_integrations) {
          const saved = portalsRes.data.portal_integrations;
          setPortals((prev) => {
            const merged = { ...prev };
            for (const slug of Object.keys(merged)) {
              if (saved[slug]) merged[slug] = { ...merged[slug], ...saved[slug] };
            }
            return merged;
          });
        }
      } catch (err) {
        console.error("[PipelineConfig] Failed to load config:", err);
        showSnackbar(
          err?.response?.data?.message || "Failed to load pipeline configuration",
          "error"
        );
      } finally {
        setLoading(false);
      }
    };
    loadAll();
  }, []);

  const handlePipelineChange = (e) => {
    const { name, value, type, checked } = e.target;
    setPipelineForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleGenerateToken = async () => {
    setGeneratingToken(true);
    try {
      const res = await API.post("/api/users/generate-service-token");
      const { token, expires_at } = res.data;
      setPipelineForm((prev) => ({ ...prev, te_api_token: token }));
      showSnackbar(`Token generated — expires ${new Date(expires_at).toLocaleDateString()}`);
    } catch (err) {
      console.error("[PipelineConfig] Failed to generate service token:", err);
      showSnackbar(err?.response?.data?.message || "Failed to generate token", "error");
    } finally {
      setGeneratingToken(false);
    }
  };

  const handleSavePipeline = async () => {
    if (pipelineForm.ts_send_credentials_via === "te_email") {
      const missing = [];
      if (!pipelineForm.nylas_grant_id) missing.push("Nylas Grant ID");
      if (!pipelineForm.nylas_api_key) missing.push("Nylas API Key");
      if (!pipelineForm.credential_email_subject) missing.push("Email Subject");
      if (!pipelineForm.credential_email_body) missing.push("Email Body");
      if (missing.length) {
        const message = `"TE Email" needs ${missing.join(", ")} filled in first`;
        console.warn(`[PipelineConfig] Save blocked — ${message}`);
        showSnackbar(message, "error");
        return;
      }
    }

    setSavingPipeline(true);
    try {
      // The credentials service emails this body as HTML, so convert the
      // plain text the user typed back to HTML before saving. Nothing else
      // in the form needs transforming.
      const payload = {
        ...pipelineForm,
        credential_email_body: plainTextToHtml(pipelineForm.credential_email_body),
      };
      await API.post("/api/config/pipeline", payload);
      console.info("[PipelineConfig] Pipeline settings saved successfully.");
      showSnackbar("Pipeline settings saved successfully");
    } catch (err) {
      console.error("[PipelineConfig] Failed to save pipeline settings:", err);
      showSnackbar(
        err?.response?.data?.message || "Failed to save pipeline settings",
        "error"
      );
    } finally {
      setSavingPipeline(false);
    }
  };

  const handlePortalToggle = (slug) =>
    setPortals((prev) => ({
      ...prev,
      [slug]: { ...prev[slug], enabled: !prev[slug].enabled },
    }));

  const handlePortalFieldChange = (slug, field, value) =>
    setPortals((prev) => ({
      ...prev,
      [slug]: { ...prev[slug], [field]: value },
    }));

  const handleSavePortals = async () => {
    setSavingPortals(true);
    try {
      await API.post("/api/config/portal-integrations", { portals });
      console.info("[PipelineConfig] Portal integrations saved successfully.");
      showSnackbar("Portal integrations saved successfully");
    } catch (err) {
      console.error("[PipelineConfig] Failed to save portal integrations:", err);
      showSnackbar(err?.response?.data?.message || "Failed to save portal integrations", "error");
    } finally {
      setSavingPortals(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 960, mx: "auto" }}>

      {/* Page Title */}
      <Box mb={3}>
        <Typography variant="h5" fontWeight={700}>Pipeline Configuration</Typography>
        
      </Box>

      {/* ── Section 1: Pipeline Settings ── */}
      <Paper elevation={1} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <SectionHeader
          icon={<SettingsIcon color="primary" />}
          title="Pipeline Settings"
          
        />

        <Grid container spacing={3}>

          {/* Row 2 — Pipeline Type + Inbound Toggle */}
          <Grid item xs={12} sm={6}>
            <FieldLabel>Default Pipeline Type</FieldLabel>
            <FormControl fullWidth size="small">
              <Select
                name="pipeline_type"
                value={pipelineForm.pipeline_type}
                onChange={handlePipelineChange}
                displayEmpty
              >
                {/* <MenuItem value="">Use Job-Level Setting</MenuItem> */}
                <MenuItem value="resume_based">White Collar (Resume Required)</MenuItem>
                <MenuItem value="non_resume_based">Blue Collar (No Resume)</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} display="flex" alignItems="flex-end" pb={0.5}>
            <FormControlLabel
              control={
                <Switch
                  name="inbound_enabled"
                  checked={pipelineForm.inbound_enabled}
                  onChange={handlePipelineChange}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight={500}>Enable Inbound Candidates</Typography>
                  
                </Box>
              }
            />
          </Grid>

          {/* Row 2a — Blue-Collar Screening Requirements (informational for Sahil's downstream processing) */}
          <Grid item xs={12}>
            <Box sx={{ border: "1px solid #e0e0e0", borderRadius: 2, p: 2.5 }}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <Typography fontWeight={700} fontSize={14}>
                  Screening Requirements
                </Typography>
              </Box>

              <Grid container spacing={2}>
                {[
                  { key: "l1_required", label: "L1 Required", color: "#f57c00" },
                  { key: "l2_required", label: "L2 Required", color: "#00897b" },
                ].map((item) => {
                  const isEnabled = pipelineForm[item.key];
                  return (
                    <Grid item xs={12} sm={6} key={item.key}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          border: `1px solid ${isEnabled ? item.color : "#e0e0e0"}`,
                          borderRadius: 2,
                          px: 2,
                          py: 1,
                          backgroundColor: isEnabled ? `${item.color}0c` : "transparent",
                          transition: "border-color 0.2s, background-color 0.2s",
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={1.5}>
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: "50%",
                              backgroundColor: isEnabled ? item.color : "#bdbdbd",
                            }}
                          />
                          <Typography fontWeight={600} fontSize={14}>
                            {item.label}
                          </Typography>
                        </Box>
                        <Switch
                          name={item.key}
                          checked={isEnabled}
                          onChange={handlePipelineChange}
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": { color: item.color },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                              backgroundColor: item.color,
                            },
                          }}
                        />
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          </Grid>

          {/* Row 2b — Send Credentials Via (TalentSprint) */}
          <Grid item xs={12} sm={6}>
            <FieldLabel>Send Credentials Via</FieldLabel>
            <FormControl fullWidth size="small">
              <Select
                name="ts_send_credentials_via"
                value={pipelineForm.ts_send_credentials_via}
                onChange={handlePipelineChange}
              >
                <MenuItem value="acx_email">ACX Email</MenuItem>
                <MenuItem value="te_email">TE Email (Nylas)</MenuItem>
                <MenuItem value="direct" disabled>Direct (coming soon)</MenuItem>
              </Select>
            </FormControl>
            {/* <Typography variant="caption" color="text.secondary" mt={0.5} display="block">
              How candidate portal credentials are delivered. "TE Email" sends via your own Nylas
              account below instead of ACX's shared inbox.
            </Typography> */}
          </Grid>

          {/* Row 2c — Per-client Nylas config + credentials email template, only when TE Email is selected */}
          {pipelineForm.ts_send_credentials_via === "te_email" && (
            <Grid item xs={12}>
              <Box
                sx={{
                  border: "1px solid #e0e0e0",
                  borderRadius: 2,
                  p: 2.5,
                  mt: 1,
                }}
              >
                <Typography variant="subtitle2" fontWeight={700} mb={2}>
                  Candidate Credentials Email (Nylas)
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FieldLabel required>Nylas Grant ID</FieldLabel>
                    <TextField
                      fullWidth
                      size="small"
                      name="nylas_grant_id"
                      value={pipelineForm.nylas_grant_id}
                      onChange={handlePipelineChange}
                      placeholder="e.g. 4c8...-grant-id"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FieldLabel required>Nylas API Key</FieldLabel>
                    <TextField
                      fullWidth
                      size="small"
                      type="password"
                      name="nylas_api_key"
                      value={pipelineForm.nylas_api_key}
                      onChange={handlePipelineChange}
                      placeholder="Paste this client's Nylas API key"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FieldLabel required>Email Subject</FieldLabel>
                    <TextField
                      fullWidth
                      size="small"
                      name="credential_email_subject"
                      value={pipelineForm.credential_email_subject}
                      onChange={handlePipelineChange}
                      placeholder="e.g. Your Training Simulation Portal Login, {{username}}"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FieldLabel required>Email Body</FieldLabel>
                    <TextField
                      fullWidth
                      multiline
                      minRows={10}
                      name="credential_email_body"
                      value={pipelineForm.credential_email_body}
                      onChange={handlePipelineChange}
                      placeholder={CREDENTIAL_EMAIL_BODY_PLACEHOLDER}
                    />
                    <Typography variant="caption" color="text.secondary" mt={0.5} display="block">
                      Write in plain text — leave a blank line to start a new paragraph;
                      single line breaks are kept. It's turned into a formatted HTML email
                      automatically when you save.
                    </Typography>
                    <Box display="flex" alignItems="center" flexWrap="wrap" gap={1} mt={1}>
                      <Typography variant="caption" color="text.secondary">
                        Insert:
                      </Typography>
                      {CREDENTIAL_EMAIL_PLACEHOLDERS.map((p) => (
                        <Chip
                          key={p.token}
                          label={p.token}
                          size="small"
                          variant="outlined"
                          title={p.hint}
                          onClick={() =>
                            setPipelineForm((prev) => ({
                              ...prev,
                              credential_email_body: `${prev.credential_email_body}${p.token}`,
                            }))
                          }
                          sx={{ fontFamily: "monospace", cursor: "pointer" }}
                        />
                      ))}
                    </Box>
                    <Typography variant="caption" color="text.secondary" mt={0.5} display="block">
                      These tokens are stored as literal text and substituted with the real
                      candidate username/email/password/URL when the email is sent — not sent by this app.
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          )}

          {/* Row 3 — Resume Score Threshold + Resume Unavailable Action */}
          <Grid item xs={12} sm={6}>
            <FieldLabel>Resume Score Threshold (%)</FieldLabel>
            <TextField
              fullWidth
              size="small"
              name="resume_score_threshold"
              type="number"
              value={pipelineForm.resume_score_threshold}
              onChange={handlePipelineChange}
              inputProps={{ min: 0, max: 100 }}
              placeholder="70"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FieldLabel>Resume Unavailable Action</FieldLabel>
            <FormControl fullWidth size="small">
              <Select
                name="resume_unavailable_action"
                value={pipelineForm.resume_unavailable_action}
                onChange={handlePipelineChange}
                displayEmpty
              >
                <MenuItem value="">None</MenuItem>
                <MenuItem value="shortlist_anyway">Shortlist Anyway</MenuItem>
                <MenuItem value="not_shortlist">Do Not Shortlist</MenuItem>
                <MenuItem value="skip">Skip</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Divider — TE API section */}
          <Grid item xs={12}>
            <Box mt={1} mb={-1}>
              <Typography variant="subtitle2" fontWeight={700} color="text.secondary" textTransform="uppercase" letterSpacing={0.8}>
                TE API Access 
              </Typography>
              <Divider sx={{ mt: 1 }} />
            </Box>
          </Grid>

          {/* Row 4 — TE URL */}
          <Grid item xs={12}>
            <FieldLabel>TE URL</FieldLabel>
            <TextField
              fullWidth
              size="small"
              name="te_api_url"
              value={pipelineForm.te_api_url}
              onChange={handlePipelineChange}
              placeholder="e.g. https://your-talentengine-host"
            />
          </Grid>

          {/* Row 5 — TE API Token + Generate button */}
          <Grid item xs={12}>
            <FieldLabel>TE API Token</FieldLabel>
            <Box display="flex" gap={1.5} alignItems="center">
              <TextField
                fullWidth
                size="small"
                name="te_api_token"
                value={pipelineForm.te_api_token}
                onChange={handlePipelineChange}
                type="password"
                placeholder="Paste existing token or generate new one below"
              />
              <Button
                variant="outlined"
                size="medium"
                onClick={handleGenerateToken}
                disabled={generatingToken}
                sx={{ whiteSpace: "nowrap", minWidth: 150, height: 40 }}
              >
                {generatingToken
                  ? <><CircularProgress size={14} sx={{ mr: 1 }} />Generating</>
                  : "Generate Token"}
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary" mt={0.5} display="block">
              Generates a service token. 
            </Typography>
          </Grid>

          {/* Voice Callback URL */}
          <Grid item xs={12}>
            <FieldLabel>Voice Callback URL</FieldLabel>
            <TextField
              fullWidth
              size="small"
              name="voice_callback_url"
              value={pipelineForm.voice_callback_url}
              onChange={handlePipelineChange}
              placeholder="e.g. https://your-voice-service-host/webhook/l1-result"
            />
            {/* <Typography variant="caption" color="text.secondary" mt={0.5} display="block">
              RF service sends L1 voice bot results to this URL.
            </Typography> */}
          </Grid>

        </Grid>

        {/* Save button */}
        <Box mt={4} display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            size="large"
            onClick={handleSavePipeline}
            disabled={savingPipeline}
            sx={{ minWidth: 180 }}
          >
            {savingPipeline
              ? <><CircularProgress size={16} sx={{ mr: 1 }} color="inherit" />Saving...</>
              : "Save Pipeline Settings"}
          </Button>
        </Box>
      </Paper>

      {/* ── Section 2: Portal Integrations ── */}
      <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
        <SectionHeader
          icon={<IntegrationInstructionsIcon color="primary" />}
          title="Portal Integrations"
          subtitle="Enable job portals and provide your own API credentials (BYOK)"
        />

        <Grid container spacing={2}>
          {PORTAL_DEFINITIONS.map((portal) => {
            const isEnabled = portals[portal.slug]?.enabled ?? false;
            return (
              <Grid item xs={12} key={portal.slug}>
                <Box
                  sx={{
                    border: `1px solid ${isEnabled ? portal.color : "#e0e0e0"}`,
                    borderRadius: 2,
                    p: 2.5,
                    transition: "border-color 0.2s",
                    backgroundColor: isEnabled ? `${portal.color}08` : "transparent",
                  }}
                >
                  {/* Portal header row */}
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          backgroundColor: isEnabled ? portal.color : "#bdbdbd",
                        }}
                      />
                      <Typography fontWeight={700} fontSize={15}>
                        {portal.label}
                      </Typography>
                      {portal.note && (
                        <Chip
                          label={portal.note}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: 11, height: 20 }}
                        />
                      )}
                    </Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isEnabled}
                          onChange={() => handlePortalToggle(portal.slug)}
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": { color: portal.color },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                              backgroundColor: portal.color,
                            },
                          }}
                        />
                      }
                      label={
                        <Typography variant="body2" color={isEnabled ? portal.color : "text.secondary"} fontWeight={600}>
                          {isEnabled ? "Enabled" : "Disabled"}
                        </Typography>
                      }
                      labelPlacement="start"
                    />
                  </Box>

                  {/* Credential fields — only shown when enabled and has fields */}
                  {isEnabled && portal.fields.length > 0 && (
                    <Grid container spacing={2} mt={1.5}>
                      {portal.fields.map((field) => (
                        <Grid item xs={12} sm={6} md={4} key={field.key}>
                          <FieldLabel>{field.label}</FieldLabel>
                          <TextField
                            fullWidth
                            size="small"
                            type={field.type || "text"}
                            value={portals[portal.slug]?.[field.key] || ""}
                            onChange={(e) =>
                              handlePortalFieldChange(portal.slug, field.key, e.target.value)
                            }
                            placeholder={`Enter ${field.label}`}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Box>
              </Grid>
            );
          })}
        </Grid>

        {/* Save button */}
        <Box mt={4} display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            size="large"
            onClick={handleSavePortals}
            disabled={savingPortals}
            sx={{ minWidth: 200 }}
          >
            {savingPortals
              ? <><CircularProgress size={16} sx={{ mr: 1 }} color="inherit" />Saving...</>
              : "Save Portal Integrations"}
          </Button>
        </Box>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PipelineConfig;
